import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageCircle, BadgeCheck, MapPin, Instagram, Trophy, UserPlus, UserCheck } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ShareBar from "../components/ShareBar";

export default function SakhiProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);
  useEffect(() => {
    setP(null); setNotFound(false); setFollowing(false);
    API.get(`/sakhi/profile/${username}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
    if (user) API.get(`/sakhi/${username}/follow-status`).then((r) => setFollowing(r.data.following)).catch(() => {});
  }, [username, user]);
  const toggleFollow = async () => {
    if (!user) { toast.error("Log in to follow 💗"); return; }
    try { const { data } = await API.post(`/sakhi/${username}/follow`); setFollowing(data.following); setP((x) => ({ ...x, followers: data.followers })); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (notFound) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="sakhi-profile-notfound">
      <h1 className="font-display text-3xl font-semibold text-stone-900">Sakhi not found</h1>
      <p className="text-stone-500 mt-2">This profile may be private or doesn't exist.</p>
      <Link to="/community" className="inline-block mt-6 text-sm font-semibold text-[#6A1B29]">Explore the community →</Link>
    </div>
  );
  if (!p) return <div className="min-h-[60vh] grid place-items-center text-stone-400">Loading…</div>;

  return (
    <div className="bg-[#FAF6F1] min-h-[70vh]" data-testid="sakhi-profile-page">
      <Seo title={`${p.name} (@${p.username}) | Suvikka Sakhi`} description={p.bio || `${p.name} is part of the Suvikka Sakhi community.`} canonicalPath={`/sakhi/${p.username}`} image={p.avatar || p.cover} />

      <div className="relative h-44 sm:h-60 bg-gradient-to-br from-[#6A1B29] to-[#501325] overflow-hidden" data-testid="sakhi-profile-cover">
        {p.cover && <img src={p.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-black/10" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="relative -mt-14 sm:-mt-16 flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl ring-4 ring-[#FAF6F1] bg-[#6A1B29] text-white grid place-items-center overflow-hidden shrink-0" data-testid="sakhi-profile-avatar">
            {p.avatar ? <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-display text-5xl">{p.name?.[0] || "S"}</span>}
          </div>
          <div className="flex-1 pb-1">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 flex items-center gap-2">{p.name}{p.is_club && <BadgeCheck className="w-6 h-6 text-[#6A1B29]" />}</h1>
            <p className="text-sm text-stone-500">@{p.username}</p>
            <div className="flex items-center gap-4 mt-1.5 text-sm" data-testid="profile-follow-stats">
              <span><b className="text-stone-900">{p.followers ?? 0}</b> <span className="text-stone-500">followers</span></span>
              <span><b className="text-stone-900">{p.following ?? 0}</b> <span className="text-stone-500">following</span></span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-stone-500">
              {p.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.city}{p.state ? `, ${p.state}` : ""}</span>}
              {p.member_since && <span>Sakhi since {p.member_since}</span>}
              {p.instagram && <a href={p.instagram.startsWith("http") ? p.instagram : `https://instagram.com/${p.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#6A1B29]" data-testid="sakhi-profile-ig"><Instagram className="w-3.5 h-3.5" /> Instagram</a>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && p.username !== user.username && (
              <button onClick={toggleFollow} data-testid="profile-follow-btn" className={`inline-flex items-center gap-1.5 font-semibold px-5 py-2.5 rounded-full text-sm transition-colors ${following ? "bg-stone-100 text-stone-700 border border-stone-300" : "bg-[#6A1B29] text-white hover:bg-[#501325]"}`}>
                {following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
              </button>
            )}
            <ShareBar compact text={`Meet ${p.name} on Suvikka Sakhi 💗`} />
          </div>
        </div>

        {p.bio && <p className="text-stone-700 mt-5 max-w-2xl" data-testid="sakhi-profile-bio">{p.bio}</p>}
        {p.interests?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {p.interests.map((t, i) => <span key={i} className="bg-white border border-stone-200 rounded-full px-3 py-1 text-xs font-medium text-stone-700" data-testid={`sakhi-interest-${i}`}>{t}</span>)}
          </div>
        )}

        {p.submissions?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-semibold text-stone-900 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#6A1B29]" /> Challenge Entries</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {p.submissions.map((s) => (
                <div key={s.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`profile-submission-${s.id}`}>
                  {s.media?.[0] && <img src={s.media[0]} alt="" loading="lazy" className="w-full aspect-square object-cover" />}
                  <div className="p-3"><p className="text-[10px] font-semibold uppercase tracking-wider text-[#6A1B29]">{s.challenge_title}</p><p className="text-xs text-stone-600 mt-1 line-clamp-2">{s.caption}</p></div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10 pb-16">
          <h2 className="font-display text-xl font-semibold text-stone-900">Posts</h2>
          {p.posts?.length === 0 ? (
            <p className="text-sm text-stone-500 mt-3 bg-white border border-stone-200 rounded-2xl p-8 text-center" data-testid="sakhi-profile-noposts">No public posts yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4" data-testid="sakhi-profile-posts">
              {p.posts.map((post) => (
                <div key={post.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                  {post.media?.[0] && <img src={post.media[0]} alt="" loading="lazy" className="w-full aspect-square object-cover" />}
                  <div className="p-3">
                    <p className="text-xs text-stone-600 line-clamp-2">{post.caption}</p>
                    <div className="flex items-center gap-4 mt-2 text-stone-400 text-xs"><span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {post.like_count}</span><span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {post.comment_count}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
